// src/services/reporting/projectReports.service.ts
import { collection, query, where, getDocs, Timestamp } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { workloadService } from '../workload.service';
import { addressService } from '../address.service';
import { projectService } from '../project.service';
import { canvasserReportsService } from './canvasserReports.service';
import {
  ProjectOverviewReport,
  DailyTrendData,
  DateRange,
  ClientExportReport,
} from '../../types/reporting.types';
import { AddressStatus } from '../../types/address.types';

class ProjectReportsService {
  async generateProjectOverview(
    projectId: string,
    dateRange?: DateRange
  ): Promise<ProjectOverviewReport | null> {
    const project = await projectService.getProject(projectId);
    if (!project) return null;

    const effectiveDateRange = dateRange || {
      start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000), // Last 30 days
      end: new Date(),
    };

    // Get all workloads
    const workloads = await workloadService.getProjectWorkloads(projectId);

    // Calculate summary metrics
    const activeWorkloads = workloads.filter(w => w.status === 'active');
    const completedWorkloads = workloads.filter(w => w.status === 'completed');

    let totalAddresses = 0;
    let addressesVisited = 0;
    let addressesCompleted = 0;
    let totalSurveyResponses = 0;

    const statusBreakdown: Record<AddressStatus, number> = {
      pending: 0,
      visited: 0,
      not_home: 0,
      refused: 0,
      completed: 0,
      discontinued: 0,
      inaccessible: 0,
    };

    // Collect unique canvassers
    const canvasserSet = new Set<string>();

    // Aggregate data from all workloads
    for (const workload of workloads) {
      // Safely access progress with defaults
      const progress = workload.progress || { totalAddresses: 0, completed: 0, surveyResponses: 0 };
      totalAddresses += progress.totalAddresses || 0;
      addressesCompleted += progress.completed || 0;

      // Add canvassers
      workload.assignedTo?.forEach(id => canvasserSet.add(id));

      // Get detailed address data for status breakdown
      try {
        const addresses = await addressService.getWorkloadAddresses(workload.id);
        addresses.forEach(addr => {
          if (addr.status && statusBreakdown[addr.status] !== undefined) {
            statusBreakdown[addr.status]++;
          }
          if (addr.status !== 'pending') {
            addressesVisited++;
          }
        });
      } catch (error) {
        console.error(`Error fetching addresses for workload ${workload.id}:`, error);
      }

      totalSurveyResponses += progress.surveyResponses || 0;
    }

    // Calculate daily trends
    const dailyTrends = await this.calculateDailyTrends(projectId, effectiveDateRange);

    // Get top canvassers
    const topCanvassers = await canvasserReportsService.getCanvasserSummaries(
      projectId,
      effectiveDateRange
    );

    // Calculate useful metrics
    const usefulMetrics = await this.calculateUsefulMetrics(workloads, statusBreakdown);

    // Estimate total field hours from canvasser data
    let totalFieldHours = 0;
    topCanvassers.forEach(c => {
      // Rough estimate: avgTimePerAddress * totalVisits / 60
      totalFieldHours += (c.avgTimePerAddress * c.totalVisits) / 60;
    });

    return {
      projectId,
      projectName: project.name,
      generatedAt: new Date(),
      dateRange: effectiveDateRange,

      summary: {
        totalWorkloads: workloads.length,
        activeWorkloads: activeWorkloads.length,
        completedWorkloads: completedWorkloads.length,
        totalAddresses,
        addressesVisited,
        addressesCompleted,
        overallCompletionRate: totalAddresses > 0
          ? Math.round((addressesCompleted / totalAddresses) * 100)
          : 0,
        totalSurveyResponses,
        totalCanvassers: canvasserSet.size,
        totalFieldHours: Math.round(totalFieldHours),
      },

      statusBreakdown,
      dailyTrends,
      topCanvassers: topCanvassers.slice(0, 10), // Top 10
      usefulMetrics,
    };
  }

  private async calculateDailyTrends(
    projectId: string,
    dateRange: DateRange
  ): Promise<DailyTrendData[]> {
    const workloads = await workloadService.getProjectWorkloads(projectId);
    const dailyData: Record<string, DailyTrendData> = {};

    // Initialize all days in range
    const currentDate = new Date(dateRange.start);
    while (currentDate <= dateRange.end) {
      const dateStr = currentDate.toISOString().split('T')[0];
      dailyData[dateStr] = {
        date: dateStr,
        addressesVisited: 0,
        addressesCompleted: 0,
        surveysCompleted: 0,
        discontinued: 0,
        refused: 0,
        notHome: 0,
        newAddressesAdded: 0,
        activeCanvassers: 0,
      };
      currentDate.setDate(currentDate.getDate() + 1);
    }

    // Aggregate visit data from addresses
    for (const workload of workloads) {
      let addresses: any[] = [];
      try {
        addresses = await addressService.getWorkloadAddresses(workload.id);
      } catch (error) {
        console.error(`Error fetching addresses for workload ${workload.id}:`, error);
        continue;
      }

      addresses.forEach(addr => {
        // Process each visit
        addr.visits?.forEach(visit => {
          const visitDate = visit.visitedAt?.toDate?.();
          if (visitDate && visitDate >= dateRange.start && visitDate <= dateRange.end) {
            const dateStr = visitDate.toISOString().split('T')[0];
            if (dailyData[dateStr]) {
              dailyData[dateStr].addressesVisited++;

              switch (visit.result || visit.outcome) {
                case 'completed':
                  dailyData[dateStr].addressesCompleted++;
                  break;
                case 'discontinued':
                  dailyData[dateStr].discontinued++;
                  break;
                case 'refused':
                  dailyData[dateStr].refused++;
                  break;
                case 'not_home':
                  dailyData[dateStr].notHome++;
                  break;
              }

              if (visit.surveyResponseId) {
                dailyData[dateStr].surveysCompleted++;
              }
            }
          }
        });

        // Check if address was field-added (by checking notes or createdAt within range)
        const createdAt = addr.createdAt?.toDate?.();
        if (createdAt && createdAt >= dateRange.start && createdAt <= dateRange.end) {
          const notes = addr.notes || '';
          if (notes.toLowerCase().includes('added by canvasser') || notes.toLowerCase().includes('unit added')) {
            const dateStr = createdAt.toISOString().split('T')[0];
            if (dailyData[dateStr]) {
              dailyData[dateStr].newAddressesAdded++;
            }
          }
        }
      });
    }

    // Calculate active canvassers per day (simplified - count unique visitors)
    // This would need visit-level user tracking for accuracy

    return Object.values(dailyData).sort((a, b) => a.date.localeCompare(b.date));
  }

  private async calculateUsefulMetrics(workloads: any[], statusBreakdown: Record<AddressStatus, number>): Promise<{
    workloadProgress: {
      total: number;
      active: number;
      completed: number;
      draft: number;
      avgCompletionRate: number;
      avgAddressesPerWorkload: number;
    };
    contactEfficiency: {
      totalAttempts: number;
      contactsMade: number;
      contactRate: number;
      notHomeCount: number;
      notHomeRate: number;
      refusedCount: number;
      refusedRate: number;
    };
    surveyMetrics: {
      totalSurveyResponses: number;
      surveyCompletionRate: number;
      addressesWithSurveys: number;
    };
  }> {
    // Workload progress
    const activeWorkloads = workloads.filter(w => w.status === 'active').length;
    const completedWorkloads = workloads.filter(w => w.status === 'completed').length;
    const draftWorkloads = workloads.filter(w => w.status === 'draft').length;

    let totalCompletionRate = 0;
    let totalAddresses = 0;

    for (const workload of workloads) {
      const progress = workload.progress || { totalAddresses: 0, completed: 0 };
      totalAddresses += progress.totalAddresses || 0;
      if (progress.totalAddresses > 0) {
        totalCompletionRate += (progress.completed || 0) / progress.totalAddresses;
      }
    }

    const avgCompletionRate = workloads.length > 0
      ? Math.round((totalCompletionRate / workloads.length) * 100)
      : 0;

    const avgAddressesPerWorkload = workloads.length > 0
      ? Math.round(totalAddresses / workloads.length)
      : 0;

    // Contact efficiency from status breakdown
    const totalVisited = Object.entries(statusBreakdown)
      .filter(([status]) => status !== 'pending')
      .reduce((sum, [_, count]) => sum + count, 0);

    const contactsMade = (statusBreakdown.completed || 0) +
                         (statusBreakdown.refused || 0) +
                         (statusBreakdown.discontinued || 0);

    const notHomeCount = statusBreakdown.not_home || 0;
    const refusedCount = statusBreakdown.refused || 0;

    const contactRate = totalVisited > 0
      ? Math.round((contactsMade / totalVisited) * 100)
      : 0;

    const notHomeRate = totalVisited > 0
      ? Math.round((notHomeCount / totalVisited) * 100)
      : 0;

    const refusedRate = totalVisited > 0
      ? Math.round((refusedCount / totalVisited) * 100)
      : 0;

    // Survey metrics
    let totalSurveyResponses = 0;
    workloads.forEach(w => {
      const progress = w.progress || { surveyResponses: 0 };
      totalSurveyResponses += progress.surveyResponses || 0;
    });

    const surveyCompletionRate = contactsMade > 0
      ? Math.round((totalSurveyResponses / contactsMade) * 100)
      : 0;

    return {
      workloadProgress: {
        total: workloads.length,
        active: activeWorkloads,
        completed: completedWorkloads,
        draft: draftWorkloads,
        avgCompletionRate,
        avgAddressesPerWorkload,
      },
      contactEfficiency: {
        totalAttempts: totalVisited,
        contactsMade,
        contactRate,
        notHomeCount,
        notHomeRate,
        refusedCount,
        refusedRate,
      },
      surveyMetrics: {
        totalSurveyResponses,
        surveyCompletionRate,
        addressesWithSurveys: totalSurveyResponses,
      },
    };
  }

  async generateClientExport(
    projectId: string,
    dateRange?: DateRange,
    anonymize: boolean = false
  ): Promise<ClientExportReport | null> {
    const overview = await this.generateProjectOverview(projectId, dateRange);
    if (!overview) return null;

    // Get survey analytics for each survey in the project
    const workloads = await workloadService.getProjectWorkloads(projectId);
    const surveyIds = new Set<string>();
    workloads.forEach(w => {
      if (w.surveyId) surveyIds.add(w.surveyId);
    });

    const surveyData: ClientExportReport['surveyData'] = [];

    // Would need to import surveyReportsService here
    // For now, provide placeholder structure

    // Generate key findings
    const keyFindings: string[] = [];

    if (overview.summary.overallCompletionRate > 80) {
      keyFindings.push(`High completion rate of ${overview.summary.overallCompletionRate}% achieved`);
    }

    if (overview.summary.totalSurveyResponses > 0) {
      keyFindings.push(`${overview.summary.totalSurveyResponses} survey responses collected`);
    }

    const contactRate = overview.summary.addressesVisited > 0
      ? Math.round(((overview.statusBreakdown.completed + overview.statusBreakdown.refused + overview.statusBreakdown.discontinued) / overview.summary.addressesVisited) * 100)
      : 0;

    if (contactRate > 50) {
      keyFindings.push(`Contact rate of ${contactRate}% indicates strong community engagement`);
    }

    // Identify high/low engagement areas (simplified)
    const areasWithHighEngagement: string[] = [];
    const areasWithLowEngagement: string[] = [];

    // Trend analysis
    const recentTrends = overview.dailyTrends.slice(-7);
    const earlierTrends = overview.dailyTrends.slice(-14, -7);

    const recentAvg = recentTrends.reduce((sum, d) => sum + d.addressesVisited, 0) / Math.max(recentTrends.length, 1);
    const earlierAvg = earlierTrends.reduce((sum, d) => sum + d.addressesVisited, 0) / Math.max(earlierTrends.length, 1);

    let responseRateTrend: 'increasing' | 'decreasing' | 'stable' = 'stable';
    if (recentAvg > earlierAvg * 1.1) {
      responseRateTrend = 'increasing';
    } else if (recentAvg < earlierAvg * 0.9) {
      responseRateTrend = 'decreasing';
    }

    // Peak activity days
    const peakActivityDays = overview.dailyTrends
      .sort((a, b) => b.addressesVisited - a.addressesVisited)
      .slice(0, 3)
      .map(d => d.date);

    // Recommendations
    const recommendations: string[] = [];

    if (overview.statusBreakdown.not_home > overview.statusBreakdown.completed) {
      recommendations.push('Consider scheduling return visits during different hours to improve contact rates');
    }

    if (overview.statusBreakdown.discontinued > overview.summary.totalSurveyResponses * 0.1) {
      recommendations.push('High discontinuation rate - consider shortening survey or improving engagement training');
    }

    return {
      projectId,
      projectName: overview.projectName,
      generatedAt: new Date(),
      dateRange: overview.dateRange,

      executiveSummary: {
        totalAddressesCanvassed: overview.summary.addressesVisited,
        totalContactsMade: overview.statusBreakdown.completed + overview.statusBreakdown.refused + overview.statusBreakdown.discontinued,
        totalSurveysCompleted: overview.summary.totalSurveyResponses,
        overallCompletionRate: overview.summary.overallCompletionRate,
        keyFindings,
      },

      surveyData,

      geographicInsights: {
        areasWithHighEngagement,
        areasWithLowEngagement,
        coverageMap: null, // Would include GeoJSON
      },

      trendAnalysis: {
        responseRateTrend,
        peakActivityDays,
        recommendations,
      },
    };
  }

  exportOverviewToCSV(report: ProjectOverviewReport): string {
    const lines: string[] = [];

    // Header
    lines.push('Project Overview Report');
    lines.push(`Project,${report.projectName}`);
    lines.push(`Generated,${report.generatedAt.toISOString()}`);
    lines.push(`Date Range,${report.dateRange.start.toISOString().split('T')[0]} to ${report.dateRange.end.toISOString().split('T')[0]}`);
    lines.push('');

    // Summary
    lines.push('Summary Metrics');
    lines.push(`Total Workloads,${report.summary.totalWorkloads}`);
    lines.push(`Active Workloads,${report.summary.activeWorkloads}`);
    lines.push(`Completed Workloads,${report.summary.completedWorkloads}`);
    lines.push(`Total Addresses,${report.summary.totalAddresses}`);
    lines.push(`Addresses Visited,${report.summary.addressesVisited}`);
    lines.push(`Addresses Completed,${report.summary.addressesCompleted}`);
    lines.push(`Overall Completion Rate,${report.summary.overallCompletionRate}%`);
    lines.push(`Survey Responses,${report.summary.totalSurveyResponses}`);
    lines.push(`Total Canvassers,${report.summary.totalCanvassers}`);
    lines.push(`Estimated Field Hours,${report.summary.totalFieldHours}`);
    lines.push('');

    // Status breakdown
    lines.push('Status Breakdown');
    Object.entries(report.statusBreakdown).forEach(([status, count]) => {
      lines.push(`${status},${count}`);
    });
    lines.push('');

    // Daily trends
    lines.push('Daily Activity');
    lines.push('Date,Visited,Completed,Surveys,Discontinued,Refused,Not Home,New Addresses');
    report.dailyTrends.forEach(day => {
      lines.push(
        `${day.date},${day.addressesVisited},${day.addressesCompleted},${day.surveysCompleted},${day.discontinued},${day.refused},${day.notHome},${day.newAddressesAdded}`
      );
    });
    lines.push('');

    // Top canvassers
    lines.push('Top Canvassers');
    lines.push('Name,Total Visits,Completed,Completion Rate,Surveys');
    report.topCanvassers.forEach(c => {
      lines.push(
        `${c.userName},${c.totalVisits},${c.completedAddresses},${c.completionRate}%,${c.surveysCompleted}`
      );
    });

    return lines.join('\n');
  }
}

export const projectReportsService = new ProjectReportsService();
